"use client";

import { useState } from "react";
import type { OpeningHour } from "@/modules/scheduling/surfaces";
import { useFormAction, type FormAction } from "./useFormAction";
import { buttonClass } from "@/design/buttons";
import { Banner } from "./ui";

/**
 * The clinic's Opening Hours grid (H40, docs/ux/screens/ClinicOpeningHoursScreen.md).
 *
 * **Hours ticked on a grid, not times typed into fields.** A Time Block is
 * exactly one hour since H39, so the grid cell and the bookable block are the
 * same thing and there is no arithmetic between what a clinic ticks and what a
 * Consumer can book. It also removes every "end before start" error that a pair
 * of time fields makes possible.
 *
 * The visible range is 06:00 to 22:00 unless asked otherwise, because a wall of
 * 168 checkboxes is not a grid anybody can scan. **Hours already open outside
 * that range are always rendered**, whatever the toggle says: an hour that is
 * open and invisible is how a clinic ends up taking bookings at 03:00 without
 * knowing.
 */

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_FROM = 6;
const DEFAULT_TO = 22;

const key = (weekday: number, hour: number) => `${weekday}:${hour}`;

/**
 * ClinicOpeningHoursScreen (docs/ux/screens/ClinicOpeningHoursScreen.md),
 * shared by both frontends (R22 T14).
 *
 * **The grid is one checkbox group posting under one name**, which is why
 * `formToJson` collapses a repeated key into an array: keeping only the last
 * ticked box would close the clinic four days a week. This is the clinic's
 * calendar for everybody who works there; one person's time off is a
 * Blackout on their own page.
 */
export function OpeningHoursEditor({
  saved,
  save,
}: {
  saved: OpeningHour[];
  save: FormAction;
}) {
  const { state, pending, onSubmit } = useFormAction(save);
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(saved.map((h) => key(h.weekday, h.hour))),
  );
  const [showAll, setShowAll] = useState(false);

  const openHours = [...open].map((k) => Number(k.split(":")[1]));
  const from = Math.min(DEFAULT_FROM, ...openHours);
  const to = Math.max(DEFAULT_TO, ...openHours.map((h) => h + 1));
  const rows = showAll
    ? Array.from({ length: 24 }, (_, i) => i)
    : Array.from({ length: to - from }, (_, i) => from + i);

  const toggle = (weekday: number, hour: number) => {
    setOpen((current) => {
      const next = new Set(current);
      const k = key(weekday, hour);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleDay = (weekday: number) => {
    setOpen((current) => {
      const next = new Set(current);
      const allOn = rows.every((hour) => next.has(key(weekday, hour)));
      for (const hour of rows) {
        if (allOn) next.delete(key(weekday, hour));
        else next.add(key(weekday, hour));
      }
      return next;
    });
  };

  /** A clinic with a uniform week ticks eight boxes instead of fifty-six. */
  const applyMondayToRest = () => {
    setOpen((current) => {
      const next = new Set<string>();
      const monday = [...current]
        .filter((k) => k.startsWith("0:"))
        .map((k) => Number(k.split(":")[1]));
      for (const k of current) if (k.startsWith("0:")) next.add(k);
      for (let weekday = 1; weekday <= 6; weekday++) {
        for (const hour of monday) next.add(key(weekday, hour));
      }
      return next;
    });
  };

  return (
    <form onSubmit={onSubmit}>
      {state.formError && <Banner tone="danger">{state.formError}</Banner>}
      {state.success && <Banner tone="success">{state.success}</Banner>}

      {open.size === 0 && (
        <p className="mb-4 text-sm text-body">
          Your clinic has no opening hours yet. Nobody can be booked until it
          does.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={applyMondayToRest}
          className={buttonClass("secondary")}
        >
          Apply Monday to the rest of the week
        </button>
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-sm font-semibold text-accent hover:underline"
        >
          {showAll ? "Show working hours only" : "Show all 24 hours"}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-1 text-center text-sm">
          <thead>
            <tr>
              <th className="w-14" />
              {DAYS.map((day, weekday) => (
                <th key={day} className="p-0">
                  <button
                    type="button"
                    onClick={() => toggleDay(weekday)}
                    title={`Open or close all of ${day}`}
                    className="w-full rounded-lg px-2 py-1 text-xs font-bold text-muted transition-colors hover:bg-page hover:text-ink"
                  >
                    {day}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((hour) => (
              <tr key={hour}>
                <th className="pe-2 text-end font-mono text-xs font-medium text-muted">
                  {String(hour).padStart(2, "0")}:00
                </th>
                {DAYS.map((day, weekday) => {
                  const on = open.has(key(weekday, hour));
                  return (
                    <td key={day} className="p-0">
                      <button
                        type="button"
                        onClick={() => toggle(weekday, hour)}
                        aria-pressed={on}
                        aria-label={`${day} ${String(hour).padStart(2, "0")}:00, ${on ? "open" : "closed"}`}
                        className={`h-8 w-full rounded-md border transition-colors ${
                          on
                            ? "border-accent bg-accent/15 text-accent"
                            : "border-line bg-surface hover:bg-page"
                        }`}
                      >
                        <span aria-hidden>{on ? "✓" : ""}</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {[...open].map((k) => (
        <input key={k} type="hidden" name="hours" value={k} />
      ))}

      <div className="mt-6 flex items-center gap-3">
        <button type="submit" disabled={pending} className={buttonClass("primary")}>
          {pending ? "Saving…" : "Save opening hours"}
        </button>
        <p className="text-xs text-muted">
          These hours apply to every practitioner here. Existing bookings keep
          their times.
        </p>
      </div>
    </form>
  );
}
